import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Zap, Users, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  delivered: '#22c55e',
  failed: '#ef4444',
  pending: '#f59e0b',
  rate: '#6366f1',
};

export default function WebhookAnalyticsChart() {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/webhooks/queue/analytics?days=${days}`, { headers: getAuthHeaders() });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error('Failed to fetch webhook analytics', e); }
    setLoading(false);
  }, [getAuthHeaders, days]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) return <div className="text-center p-6 text-gray-400">Loading analytics...</div>;
  if (!data) return null;

  const hasActivity = data.daily.some(d => d.total > 0);
  const totalDelivered = data.daily.reduce((s, d) => s + d.delivered, 0);
  const totalFailed = data.daily.reduce((s, d) => s + d.failed, 0);
  const totalAll = totalDelivered + totalFailed;
  const overallRate = totalAll > 0 ? ((totalDelivered / totalAll) * 100).toFixed(1) : '100.0';

  const pieData = [
    { name: 'Delivered', value: totalDelivered, color: COLORS.delivered },
    { name: 'Failed', value: totalFailed, color: COLORS.failed },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6" data-testid="webhook-analytics-chart">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-500">Period</span>
        </div>
        <div className="flex items-center gap-1 glass rounded-xl p-1">
          {[7, 14, 30].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${days === d ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
              data-testid={`analytics-period-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="glass hover-lift transition-all" data-testid="analytics-total-delivered">
          <CardContent className="p-3 text-center">
            <TrendingUp className="w-5 h-5 mx-auto text-green-500 mb-1" />
            <p className="text-2xl font-bold text-green-700">{totalDelivered}</p>
            <p className="text-xs text-gray-500">Delivered</p>
          </CardContent>
        </Card>
        <Card className="glass hover-lift transition-all" data-testid="analytics-total-failed">
          <CardContent className="p-3 text-center">
            <Zap className="w-5 h-5 mx-auto text-red-500 mb-1" />
            <p className="text-2xl font-bold text-red-700">{totalFailed}</p>
            <p className="text-xs text-gray-500">Failed</p>
          </CardContent>
        </Card>
        <Card className="glass hover-lift transition-all" data-testid="analytics-success-rate">
          <CardContent className="p-3 text-center">
            <TrendingUp className="w-5 h-5 mx-auto text-indigo-500 mb-1" />
            <p className="text-2xl font-bold text-indigo-700">{overallRate}%</p>
            <p className="text-xs text-gray-500">Success Rate</p>
          </CardContent>
        </Card>
        <Card className="glass hover-lift transition-all" data-testid="analytics-avg-attempts">
          <CardContent className="p-3 text-center">
            <Users className="w-5 h-5 mx-auto text-amber-500 mb-1" />
            <p className="text-2xl font-bold text-amber-700">{data.avg_attempts_to_deliver || '0'}</p>
            <p className="text-xs text-gray-500">Avg Attempts</p>
          </CardContent>
        </Card>
      </div>

      {hasActivity ? (
        <>
          {/* Daily Delivery Bar Chart */}
          <Card className="glass">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">Daily Webhook Deliveries</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.daily} barGap={2}>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 8px 30px rgba(0,0,0,.12)', backdropFilter: 'blur(8px)' }}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="delivered" name="Delivered" fill={COLORS.delivered} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="failed" name="Failed" fill={COLORS.failed} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="pending" name="Pending" fill={COLORS.pending} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Success Rate Trend */}
          <Card className="glass">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">Success Rate Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={data.daily}>
                  <defs>
                    <linearGradient id="rateGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.rate} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={COLORS.rate} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                  <Tooltip
                    formatter={(val) => [`${val}%`, 'Success Rate']}
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 8px 30px rgba(0,0,0,.12)' }}
                  />
                  <Area type="monotone" dataKey="success_rate" stroke={COLORS.rate} fill="url(#rateGrad)" strokeWidth={2} dot={{ r: 3 }} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Distribution Pie + Vendor Table */}
          <div className="grid md:grid-cols-2 gap-4">
            {pieData.length > 0 && (
              <Card className="glass">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-700">Delivery Distribution</CardTitle>
                </CardHeader>
                <CardContent className="flex justify-center">
                  <ResponsiveContainer width={200} height={200}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={3}>
                        {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: '12px', border: 'none' }} />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {data.vendors.length > 0 && (
              <Card className="glass">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-700">Vendor Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {data.vendors.map(v => (
                      <div key={v.vendor_id} className="flex items-center justify-between">
                        <span className="text-xs font-mono text-gray-600 truncate max-w-[120px]">{v.vendor_id}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-green-600">{v.delivered} ok</span>
                          <span className="text-xs text-red-600">{v.failed} fail</span>
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div className="bg-green-500 h-2 rounded-full" style={{ width: `${v.success_rate}%` }} />
                          </div>
                          <span className="text-xs font-semibold text-gray-700 w-12 text-right">{v.success_rate}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      ) : (
        /* Empty State */
        <Card className="glass">
          <CardContent className="py-12 text-center">
            <TrendingUp className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No webhook activity yet</p>
            <p className="text-sm text-gray-400 mt-1">Charts will appear once vendor webhooks start flowing</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
